export type CoachLanguage = 'en' | 'es';

type Pair = { en: string; es: string };

export type CoachChoice = {
  id: string;
  label: Pair;
  next: string;
};

export type CoachAsk = {
  id: string;
  prompt: Pair;
  choices: CoachChoice[];
};

export type CoachOutcome = {
  id: string;
  write: Pair;
  why: Pair;
};

export type CoachScript = {
  start: string;
  asks: Record<string, CoachAsk>;
  outcomes: Record<string, CoachOutcome>;
};

export type CoachMode = 'decide' | 'do';

const DECIDE_IDS = new Set([
  'fci-cui-determination',
  'data-impact-cloud-bar',
  'government-cloud-bar',
  'site-hipaa-scope',
  'site-no-cui-on-public',
  'coppa-audience',
  'pci-if-payments',
  'edu-cui-classify',
]);

const pair = (en: string, es: string): Pair => ({ en, es });

const yesNo = (yesNext: string, noNext: string): CoachChoice[] => [
  { id: 'yes', label: pair('Yes', 'Sí'), next: yesNext },
  { id: 'no', label: pair('No', 'No'), next: noNext },
];

const fciScript: CoachScript = {
  start: 'classified',
  asks: {
    classified: {
      id: 'classified',
      prompt: pair(
        'Does the job paper say Secret, Top Secret, or a facility clearance?',
        '¿El papel del trabajo dice Secret, Top Secret o una habilitación de instalación?',
      ),
      choices: yesNo('out-classified', 'cui-words'),
    },
    'cui-words': {
      id: 'cui-words',
      prompt: pair(
        'Does that paper say CUI, Controlled Unclassified Information, DFARS 252.204-7012, NIST SP 800-171, or CMMC Level 2? Or will the person use a government computer that holds CUI?',
        '¿Ese papel dice CUI, Controlled Unclassified Information, DFARS 252.204-7012, NIST SP 800-171 o CMMC Nivel 2? ¿O la persona usará una computadora del gobierno que tiene CUI?',
      ),
      choices: yesNo('out-cui', 'everyday'),
    },
    everyday: {
      id: 'everyday',
      prompt: pair(
        'Is the work unclassified IT help or staffing?',
        '¿El trabajo es ayuda de TI o personal sin clasificación?',
      ),
      choices: yesNo('out-fci', 'out-unsure'),
    },
  },
  outcomes: {
    'out-classified': {
      id: 'out-classified',
      write: pair('Classified', 'Clasificado'),
      why: pair(
        'The paper says Secret, Top Secret, or a facility clearance. That is above CUI. Do not write FCI or CUI. A person’s clearance is not the company’s facility clearance (FCL). Do not bid until the company holds what the notice asks for.',
        'El papel dice Secret, Top Secret o una habilitación de instalación. Eso está por encima de CUI. No escriba FCI ni CUI. La habilitación de una persona no es la habilitación de instalaciones (FCL) de la empresa. No oferte hasta que la empresa tenga lo que pide el aviso.',
      ),
    },
    'out-cui': {
      id: 'out-cui',
      write: pair('CUI', 'CUI'),
      why: pair(
        'The paper names CUI or the person will use a government computer that holds CUI. That path is NIST 800-171 / CMMC Level 2. It is still not classified and it is not a facility clearance.',
        'El papel nombra CUI o la persona usará una computadora del gobierno que tiene CUI. Esa ruta es NIST 800-171 / CMMC Nivel 2. Todavía no es clasificado y no es una habilitación de instalaciones.',
      ),
    },
    'out-fci': {
      id: 'out-fci',
      write: pair('FCI', 'FCI'),
      why: pair(
        'Those CUI words are not in the paper, and the work is everyday IT help or staffing. The first bar is CMMC Level 1. You do not need CMMC Level 2 or a facility clearance for this label alone.',
        'Esas palabras de CUI no están en el papel, y el trabajo es ayuda de TI o personal de todos los días. La primera barra es CMMC Nivel 1. Solo con esta etiqueta no necesita CMMC Nivel 2 ni una habilitación de instalaciones.',
      ),
    },
    'out-unsure': {
      id: 'out-unsure',
      write: pair('Not sure yet. Read the paper again. Do not guess.', 'Aún no está claro. Lea el papel otra vez. No adivine.'),
      why: pair(
        'The paper did not match the classified words, the CUI words, or everyday IT help. Open the solicitation again before you pick FCI, CUI, or Classified.',
        'El papel no coincidió con las palabras de clasificado, las de CUI ni con ayuda de TI de todos los días. Abra otra vez la solicitud antes de elegir FCI, CUI o Clasificado.',
      ),
    },
  },
};

const cloudScript: CoachScript = {
  start: 'hosted',
  asks: {
    hosted: {
      id: 'hosted',
      prompt: pair(
        'Will a government agency store its records in this product?',
        '¿Una agencia de gobierno guardará sus registros en este producto?',
      ),
      choices: yesNo('harm', 'out-consulting'),
    },
    harm: {
      id: 'harm',
      prompt: pair(
        'If those records leaked, would the harm be serious day-to-day harm, or law-enforcement / national-security harm?',
        'Si esos registros se filtran, ¿el daño es serio del día a día, o es de orden público / seguridad nacional?',
      ),
      choices: [
        { id: 'normal', label: pair('Serious day-to-day harm', 'Daño serio del día a día'), next: 'out-normal' },
        { id: 'high', label: pair('Law enforcement or national security', 'Orden público o seguridad nacional'), next: 'out-high' },
      ],
    },
  },
  outcomes: {
    'out-consulting': {
      id: 'out-consulting',
      write: pair('Consulting only. No agency stores records in this product.', 'Solo consultoría. Ninguna agencia guarda registros en este producto.'),
      why: pair(
        'A labor or IT service that does not host agency records does not pick a cloud authorization.',
        'Un servicio de personal o de TI que no hospeda registros de agencia no elige una autorización de nube.',
      ),
    },
    'out-normal': {
      id: 'out-normal',
      write: pair('Normal. TX-RAMP Level 2 or FedRAMP Moderate. A city or school may ask for SOC 2 instead.', 'Normal. TX-RAMP Nivel 2 o FedRAMP Moderate. Una ciudad o una escuela puede pedir SOC 2.'),
      why: pair(
        'Everyday confidential agency records are not the public Low bar.',
        'Los registros confidenciales de todos los días no van en la barra pública Low.',
      ),
    },
    'out-high': {
      id: 'out-high',
      write: pair('High. FedRAMP High. Texas stays at TX-RAMP Level 2 and then adds extra controls.', 'Alto. FedRAMP High. Texas se queda en TX-RAMP Nivel 2 y luego añade controles extra.'),
      why: pair(
        'Law-enforcement or national-security records are the high bar.',
        'Los registros de orden público o de seguridad nacional son la barra alta.',
      ),
    },
  },
};

const oneQuestion = (
  prompt: Pair,
  yes: CoachOutcome,
  no: CoachOutcome,
): CoachScript => ({
  start: 'only',
  asks: {
    only: {
      id: 'only',
      prompt,
      choices: yesNo('out-yes', 'out-no'),
    },
  },
  outcomes: {
    'out-yes': yes,
    'out-no': no,
  },
});

export const COACH_SCRIPTS: Record<string, CoachScript> = {
  'fci-cui-determination': fciScript,
  'edu-cui-classify': fciScript,
  'data-impact-cloud-bar': cloudScript,
  'government-cloud-bar': cloudScript,
  'site-hipaa-scope': oneQuestion(
    pair(
      'Does this website collect health information, such as appointments, symptoms, insurance, or medical records?',
      '¿Este sitio recoge información de salud, como citas, síntomas, seguro o expedientes médicos?',
    ),
    {
      id: 'out-yes',
      write: pair(
        'Health site. Publish a Notice of Privacy Practices. No ad tags on those pages unless the vendor signed a business associate agreement.',
        'Sitio de salud. Publique un Aviso de prácticas de privacidad. Sin etiquetas de anuncios en esas páginas salvo que el proveedor haya firmado un acuerdo de socio comercial.',
      ),
      why: pair(
        'The site collects health information, so the health-privacy rules apply.',
        'El sitio recoge información de salud, así que aplican las reglas de privacidad de salud.',
      ),
    },
    {
      id: 'out-no',
      write: pair('Not a health site.', 'No es un sitio de salud.'),
      why: pair(
        'The site does not collect health information.',
        'El sitio no recoge información de salud.',
      ),
    },
  ),
  'site-no-cui-on-public': oneQuestion(
    pair(
      'Are contract files, system diagrams, or CUI on this public website?',
      '¿Hay archivos de contrato, diagramas de sistemas o CUI en este sitio público?',
    ),
    {
      id: 'out-yes',
      write: pair(
        'Move those files to the government project. This public site is not the place.',
        'Mueva esos archivos al proyecto de gobierno. Este sitio público no es el lugar.',
      ),
      why: pair(
        'A public website is for everyone on the internet.',
        'Un sitio público es para todas las personas en internet.',
      ),
    },
    {
      id: 'out-no',
      write: pair('Public site only.', 'Solo sitio público.'),
      why: pair(
        'Nothing sensitive is hosted on the public site.',
        'No hay nada sensible hospedado en el sitio público.',
      ),
    },
  ),
  'coppa-audience': oneQuestion(
    pair(
      'Is this site for children under 13, or does it knowingly collect their data?',
      '¿Este sitio es para menores de 13 años, o recoge sus datos a sabiendas?',
    ),
    {
      id: 'out-yes',
      write: pair(
        'Children under 13 are in scope. Add notice, parent consent, and an age gate.',
        'Los menores de 13 años están en alcance. Añada aviso, consentimiento de los padres y una puerta de edad.',
      ),
      why: pair(
        'A site aimed at young children has extra rules before it collects their data.',
        'Un sitio dirigido a niños pequeños tiene reglas extra antes de recoger sus datos.',
      ),
    },
    {
      id: 'out-no',
      write: pair('Not for children under 13.', 'No es para menores de 13 años.'),
      why: pair(
        'The site is not aimed at children under 13 and does not knowingly collect their data.',
        'El sitio no está dirigido a menores de 13 años y no recoge sus datos a sabiendas.',
      ),
    },
  ),
  'pci-if-payments': oneQuestion(
    pair(
      'Does this site take payment card numbers?',
      '¿Este sitio toma números de tarjeta de pago?',
    ),
    {
      id: 'out-yes',
      write: pair(
        'Cards are in scope. Record the PCI form type and the company that processes the cards. Do not store card numbers here.',
        'Las tarjetas están en alcance. Anote el tipo de formulario PCI y la empresa que procesa las tarjetas. No guarde números de tarjeta aquí.',
      ),
      why: pair(
        'Taking card numbers puts the site in card-security scope.',
        'Tomar números de tarjeta pone el sitio en el alcance de seguridad de tarjetas.',
      ),
    },
    {
      id: 'out-no',
      write: pair('No payment cards on this site.', 'No hay tarjetas de pago en este sitio.'),
      why: pair(
        'The site does not take card numbers.',
        'El sitio no toma números de tarjeta.',
      ),
    },
  ),
};

export const itemCoachMode = (itemId: string): CoachMode => (
  DECIDE_IDS.has(itemId) ? 'decide' : 'do'
);

export const coachText = (line: Pair, language: CoachLanguage): string => (
  language === 'es' ? line.es : line.en
);

export type CoachTurn =
  | { kind: 'ask'; ask: CoachAsk; index: number; total: number }
  | { kind: 'answer'; outcome: CoachOutcome };

const askOrder = (script: CoachScript): string[] => Object.keys(script.asks);

export const coachAsk = (script: CoachScript, askId: string): CoachTurn => {
  const ask = script.asks[askId] || script.asks[script.start];
  const order = askOrder(script);
  const index = Math.max(0, order.indexOf(ask.id));
  return { kind: 'ask', ask, index, total: order.length };
};

export const coachAnswer = (script: CoachScript, askId: string, choiceId: string): CoachTurn => {
  const ask = script.asks[askId] || script.asks[script.start];
  const choice = ask.choices.find((row) => row.id === choiceId) || ask.choices[0];
  const outcome = script.outcomes[choice.next];
  if (outcome) {
    return { kind: 'answer', outcome };
  }
  return coachAsk(script, choice.next);
};

export type CoachDoStep = {
  text: string;
  see: string;
  index: number;
  total: number;
  last: boolean;
};

export const coachSeeLine = (step: string, language: CoachLanguage): string => {
  if (/https?:\/\//i.test(step)) {
    return language === 'es' ? 'Se abre una página de esa dirección.' : 'A page from that address opens.';
  }
  if (/\b(click|press|pulse|choose|elija|abra|open)\b/i.test(step)) {
    return language === 'es' ? 'Llega a la página siguiente.' : 'You land on the next page.';
  }
  if (/\b(print|imprima)\b/i.test(step)) {
    return language === 'es' ? 'Puede ver las palabras que hay que imprimir.' : 'You can see the words you need to print.';
  }
  if (/\b(write|type|escriba)\b/i.test(step)) {
    return language === 'es' ? 'Esas palabras quedan en la casilla.' : 'Those words are in the box.';
  }
  return language === 'es' ? 'Ese paso quedó hecho.' : 'That step is done.';
};

export const coachDoStep = (
  steps: string[],
  index: number,
  language: CoachLanguage,
): CoachDoStep | null => {
  const clean = steps.map((step) => step.trim()).filter(Boolean);
  if (!clean.length) {
    return null;
  }
  const safe = Math.min(Math.max(index, 0), clean.length - 1);
  return {
    text: clean[safe],
    see: coachSeeLine(clean[safe], language),
    index: safe,
    total: clean.length,
    last: safe === clean.length - 1,
  };
};
